/**
 * Modelo de formulario y validaciones de Packs de Energía (US-2.4) — Admin Web.
 *
 * La red y el contrato viven en `packApi`/`@goelectric/shared-types`; acá está el
 * estado editable de la vista y la conversión al body del contrato.
 *
 * Reglas acordadas con la US 2.4 (alineadas con la validación del backend):
 * - `name` obligatorio, máximo 200 caracteres.
 * - `kwh` > 0 (mínimo 0,01 para que la BD no redondee a 0).
 * - `priceCop` >= 0.
 * - `validTo` opcional; si existe, debe ser **posterior** a `validFrom`.
 * - `currency` ISO 4217 de 3 letras mayúsculas (default COP).
 * - `status` por defecto ACTIVE en el alta; editable solo en la edición.
 */
import dayjs, { type Dayjs } from 'dayjs';
import type {
  CreatePackBody,
  EnergyPack,
  EnergyPackStatus,
  UpdatePackBody,
} from '@goelectric/shared-types';

/* ========================================================================= */
/* Constantes del formulario                                                 */
/* ========================================================================= */

/** Nombre máximo aceptado por el backend. */
export const PACK_NAME_MAX = 200;

/** kWh mínimo aceptado (la columna NUMERIC(10,2) redondea por debajo a 0). */
export const PACK_KWH_MIN = 0.01;

/** kWh máximo aceptado por la columna NUMERIC(10,2). */
export const PACK_KWH_MAX = 99_999_999.99;

/** Precio máximo aceptado por la columna NUMERIC(12,2). */
export const PACK_PRICE_MAX = 9_999_999_999.99;

/** Moneda por defecto del catálogo. */
export const PACK_DEFAULT_CURRENCY = 'COP';

/** ISO 4217: exactamente 3 letras mayúsculas. */
export const CURRENCY_PATTERN = /^[A-Z]{3}$/;

/* ========================================================================= */
/* Estado del formulario                                                     */
/* ========================================================================= */

/** Valores editables; los numéricos admiten `null` mientras el usuario no escribe. */
export interface PackFormValues {
  name: string;
  kwh?: number | null;
  priceCop?: number | null;
  currency: string;
  /** Fechas del `DatePicker` (se convierten a ISO al enviar). */
  validFrom?: Dayjs | null;
  validTo?: Dayjs | null;
  status: EnergyPackStatus;
}

/** Estado inicial del alta: ACTIVE y moneda COP. */
export function emptyPackFormValues(): PackFormValues {
  return {
    name: '',
    kwh: null,
    priceCop: null,
    currency: PACK_DEFAULT_CURRENCY,
    validFrom: null,
    validTo: null,
    status: 'ACTIVE',
  };
}

/** Mapea la entidad del contrato al estado del formulario de edición. */
export function packToFormValues(pack: EnergyPack): PackFormValues {
  return {
    name: pack.name,
    kwh: pack.kwh,
    priceCop: pack.priceCop,
    currency: pack.currency || PACK_DEFAULT_CURRENCY,
    validFrom: pack.validFrom ? dayjs(pack.validFrom) : null,
    validTo: pack.validTo ? dayjs(pack.validTo) : null,
    status: pack.status,
  };
}

/* ========================================================================= */
/* Conversión al body del contrato                                           */
/* ========================================================================= */

/** Normaliza el código de moneda (recorta y pasa a mayúsculas). */
export function normalizeCurrency(value: string | null | undefined): string {
  return (value ?? '').trim().toUpperCase();
}

/** `Dayjs` válido a ISO 8601; vacío/inválido → `undefined`. */
export function dayjsToIso(value: Dayjs | null | undefined): string | undefined {
  if (!value || !value.isValid()) return undefined;
  return value.toISOString();
}

function baseBody(values: PackFormValues) {
  const validFrom = dayjsToIso(values.validFrom);
  const validTo = dayjsToIso(values.validTo);
  return {
    name: values.name.trim(),
    kwh: values.kwh ?? 0,
    priceCop: values.priceCop ?? 0,
    currency: normalizeCurrency(values.currency) || PACK_DEFAULT_CURRENCY,
    ...(validFrom ? { validFrom } : {}),
    ...(validTo ? { validTo } : {}),
  };
}

/** Body de `POST /api/v1/packs` (alta con estado, ACTIVE por defecto). */
export function toCreatePackBody(values: PackFormValues): CreatePackBody {
  return {
    ...baseBody(values),
    status: values.status ?? 'ACTIVE',
  };
}

/**
 * Body de `PATCH /api/v1/packs/{packId}`. `validTo` vacío se envía como `null`
 * para limpiar la caducidad (vigencia abierta), tal como define el contrato.
 */
export function toUpdatePackBody(values: PackFormValues): UpdatePackBody {
  return {
    ...baseBody(values),
    validTo: dayjsToIso(values.validTo) ?? null,
    status: values.status,
  };
}

/* ========================================================================= */
/* Validaciones                                                              */
/* ========================================================================= */

/** Problema de validación asociado a un campo del formulario. */
export interface PackValidationIssue {
  field: string;
  message: string;
}

/** Path de antd (`NamePath`) derivado de una ruta simple. */
export type PackFieldPath = (string | number)[];

/** Convierte una ruta simple en el path que `form.setFields` entiende. */
export function toAntdFieldPath(field: string): PackFieldPath {
  return field.split('.').filter(Boolean);
}

/**
 * `true` si `validTo` es posterior al inicio de vigencia. Si no se informó
 * `validFrom`, el backend usa `now` como inicio: se compara contra ese mismo
 * instante para no enviar un pack que nacería ya vencido.
 */
export function isValidVigencia(
  validFrom: Dayjs | null | undefined,
  validTo: Dayjs | null | undefined,
  now: Dayjs = dayjs(),
): boolean {
  if (!validTo) return true;
  if (!validTo.isValid()) return false;
  const from = validFrom ?? now;
  if (!from.isValid()) return false;
  return validTo.valueOf() > from.valueOf();
}

/**
 * Valida el formulario completo. Devuelve la lista de problemas con su campo;
 * usada por los tests de reglas y como red de seguridad antes de enviar. `now`
 * permite fijar el instante de referencia en los tests (por defecto, el actual).
 */
export function validatePackFormValues(
  values: PackFormValues,
  now: Dayjs = dayjs(),
): PackValidationIssue[] {
  const issues: PackValidationIssue[] = [];
  const name = values.name?.trim() ?? '';

  if (!name) {
    issues.push({ field: 'name', message: 'Escribe el nombre del pack.' });
  } else if (name.length > PACK_NAME_MAX) {
    issues.push({
      field: 'name',
      message: `El nombre no puede superar ${PACK_NAME_MAX} caracteres.`,
    });
  }

  if (values.kwh === undefined || values.kwh === null) {
    issues.push({ field: 'kwh', message: 'Ingresa los kWh del pack.' });
  } else if (values.kwh < PACK_KWH_MIN) {
    issues.push({ field: 'kwh', message: 'Los kWh deben ser mayores que 0.' });
  } else if (values.kwh > PACK_KWH_MAX) {
    issues.push({
      field: 'kwh',
      message: `Los kWh no pueden superar ${PACK_KWH_MAX}.`,
    });
  }

  if (values.priceCop === undefined || values.priceCop === null) {
    issues.push({ field: 'priceCop', message: 'Ingresa el precio del pack.' });
  } else if (values.priceCop < 0) {
    issues.push({ field: 'priceCop', message: 'El precio debe ser mayor o igual a 0.' });
  } else if (values.priceCop > PACK_PRICE_MAX) {
    issues.push({
      field: 'priceCop',
      message: `El precio no puede superar ${PACK_PRICE_MAX}.`,
    });
  }

  if (!CURRENCY_PATTERN.test(normalizeCurrency(values.currency))) {
    issues.push({
      field: 'currency',
      message: 'La moneda debe ser un código ISO 4217 de 3 letras (por ejemplo COP).',
    });
  }

  if (!isValidVigencia(values.validFrom, values.validTo, now)) {
    issues.push({
      field: 'validTo',
      message: 'La fecha de fin debe ser posterior a la fecha de inicio.',
    });
  }

  return issues;
}
